import { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FolderIcon } from '@heroicons/react/24/outline';
import { Hub } from '../../types/hub';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { plain, resourceWord } from '../../utils/cardFacts';
import { hubPlaceHref } from '../../utils/hubChapter';
import { placePath } from '../../utils/placePaths';
import { openPlace, usePlaceOrigin } from '../../utils/placeMotion';

interface HubCardProps {
  hub: Hub;
}

export default function HubCard({ hub }: HubCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const to = hubPlaceHref(hub);
  usePlaceOrigin(cardRef, 'hub', hub.slug, 'card');
  const about = plain(hub.description);
  const program = hub.program?.slug ? hub.program : null;
  const programTo = program ? placePath(program) : '';

  return (
    <Card ref={cardRef} className="!p-4 w-full max-w-xs">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-semibold text-primary">
            <FolderIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
            Toolkit
          </p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{hub.title}</h3>
          {program && (
            <p className="mt-1 text-sm">
              <Link
                to={programTo}
                onClick={(event) => {
                  openPlace(event, event.currentTarget, { kind: 'program', slug: program.slug }, programTo, navigate, location);
                }}
                className="font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
              >
                In {program.title}
              </Link>
            </p>
          )}
        </div>
        <SaveToMyOrbconButton
          type="HUB"
          hubId={hub.id}
          title={hub.title}
          slug={hub.slug}
          variant="icon"
          align="end"
        />
      </div>
      {about && <p className="mt-2 text-sm text-gray-600">{about}</p>}
      <p className="mt-4 text-sm text-gray-600">{resourceWord(hub.resources.length)}</p>
      <div className="mt-4">
        <QuietAction
          to={to}
          label={`Open toolkit ${hub.title}`}
          onClick={(event) => {
            openPlace(event, cardRef.current, { kind: 'hub', slug: hub.slug, via: 'card' }, to, navigate, location);
          }}
        >
          Open toolkit
        </QuietAction>
      </div>
    </Card>
  );
}
